import React, { useState } from "react";
import { useDispatch } from "react-redux";
import CookieSettingsContainer from "web/react/components/cookie-settings-container/cookie-settings-container";
import { openOverlay } from "web/redux/ducks/cookie-settings";
import { FooterSerializer } from "web/types/serializers";
import FooterBlock from "./footer-block";
import FooterExtraLinks from "./footer-extra-links";
import FooterHelpInfo from "./footer-help-info";
import FooterInternationalLinks from "./footer-i18n-links";
import FooterLeadContent from "./footer-lead-content";
import styles from "./footer.module.css";

export interface FooterProps extends ReactComponentProps {
    data: FooterSerializer;
    helpCenterUrls: string[];
    showCookieSettingsOption: boolean;
    country: string;
}

export interface FooterContextProps extends FooterProps {
    openBlockId: string | null;
    setOpenBlock: (blockId: string) => void;
    openCookieSettingsModal: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const FooterContext = React.createContext<FooterContextProps | undefined>(undefined);

export function useFooterContext(): FooterContextProps {
    const context = React.useContext(FooterContext);

    if (!context) {
        throw new Error("useFooterContext must be used within a FooterContextProvider");
    }

    return context;
}

function Footer({
    data,
    helpCenterUrls,
    showCookieSettingsOption,
    country,
    children,
}: FooterProps): JSX.Element {
    const dispatch = useDispatch();
    const [openBlockId, setOpenBlockId] = useState<string | null>(null);
    function setOpenBlock(blockId: string): void {
        if (blockId === openBlockId) {
            // close everything
            setOpenBlockId(null);
        } else {
            // Set the new active block
            setOpenBlockId(blockId);
        }
    }
    function openCookieSettingsModal(event: React.MouseEvent<HTMLAnchorElement>): void {
        event.preventDefault();
        dispatch(openOverlay());
    }

    return (
        <FooterContext.Provider
            value={{
                data,
                helpCenterUrls,
                showCookieSettingsOption,
                country,
                openBlockId,
                setOpenBlock,
                openCookieSettingsModal,
            }}
        >
            <div className={styles.footerWrap}>
                <CookieSettingsContainer />
                <footer className={styles.footer}>{children}</footer>
            </div>
        </FooterContext.Provider>
    );
}

Footer.Block = FooterBlock;
Footer.LeadContent = FooterLeadContent;
Footer.InternationalLinks = FooterInternationalLinks;
Footer.ExtraLinks = FooterExtraLinks;
Footer.HelpInfo = FooterHelpInfo;

export default Footer;
